import { useInjection } from 'inversify-react';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { IOverlayLoaderStore } from '../../../../../../../modules/common/OverlayLoader.store';
import { ImageFiltersForm } from '../../components/filters/ImageFiltersForm';
import {
  EmptyAnnotationToolFilter,
  FilterTabType,
  IAnnotationToolFilter,
  IImageFilters,
  ImageDefaultFilters,
  ISavedFilter,
  ISorting,
} from '../../imageFilters.model';
import { ImageAnnotationStatus, ImageSetType } from '../../projectDetailsImages.model';
import { IProjectDetailsImagesStore } from '../../projectDetailsImages.store';
import { getActiveFiltersCount } from '../../services/imageFilterHelper';
import { IImageFilterService } from '../../services/imageFilters.service';

interface IProps {
  onAfterFilter: () => void;
  isModalOpen: boolean;
}

export const ImageFiltersFormContainer = observer((props: IProps): ReactElement => {
  const imageFilterService = useInjection(IImageFilterService.$);
  const imagesStore = useInjection(IProjectDetailsImagesStore.$);
  const overlayLoaderStore = useInjection(IOverlayLoaderStore.$);

  const [selectedFilterTab, setSelectedFilterTab] = useState<FilterTabType>(FilterTabType.InDatasets);
  const [filtersDraft, setFiltersDraft] = useState<IImageFilters>(cloneDeep(imagesStore.imageFilters));
  const [showClearAllConfirmed, setShowClearAllConfirmed] = useState<boolean>(false);

  const handleFilterTabSelected = (selectedFilterTab: FilterTabType): void => {
    setSelectedFilterTab(selectedFilterTab);
  };

  const handleSetInDatasets = (datasets: string[]): void => {
    setFiltersDraft((prev) => ({ ...prev, datasets }));
  };

  const handleSetAnnotatedBy = (annotatedBy: string[]): void => {
    setFiltersDraft((prev) => ({ ...prev, annotatedBy }));
  };

  const handleSetReviewedBy = (reviewedBy: string[]): void => {
    setFiltersDraft((prev) => ({ ...prev, reviewedBy }));
  };

  const handleSetStatuses = (annotationStatuses: ImageAnnotationStatus[]): void => {
    setFiltersDraft((prev) => ({ ...prev, annotationStatuses }));
  };

  const handleSetImageSets = (imageSets: ImageSetType[]): void => {
    setFiltersDraft((prev) => ({ ...prev, imageSets }));
  };

  const handleSetCorrected = (corrected: boolean[]): void => {
    setFiltersDraft((prev) => ({ ...prev, corrected }));
  };

  const handleSetAnnotationTools = (annotationTools: IAnnotationToolFilter[]): void => {
    setFiltersDraft((prev) => ({ ...prev, annotationTools }));
  };

  const handleSetSorting = (sorting: ISorting[]): void => {
    setFiltersDraft((prev) => ({ ...prev, sorting }));
  };

  const handleClearAll = (): void => {
    setShowClearAllConfirmed(true);
  };

  const handleClearAllDeclined = (): void => {
    setShowClearAllConfirmed(false);
  };

  const handleClearAllConfirmedAsync = async (): Promise<void> => {
    setFiltersDraft(ImageDefaultFilters);
    setShowClearAllConfirmed(false);
    await imageFilterService.clearAllAsync();
    props.onAfterFilter();
  };

  const handleApplyAsync = async (): Promise<void> => {
    setFiltersDraft((prev) => ({
      ...prev,
      sorting: prev.sorting.filter((x) => x.by !== undefined),
    }));
    await imageFilterService.applyAsync(filtersDraft);
    props.onAfterFilter();
  };

  const handleSavedFilterLoad = (savedFilter: ISavedFilter): void => {
    setFiltersDraft(
      cloneDeep({
        ...savedFilter.filters,
        ...(!savedFilter.filters.annotationTools.length && { annotationTools: [{ ...EmptyAnnotationToolFilter }] }),
      })
    );
  };

  const handleSavedFilterAsync = async (name: string): Promise<void> => {
    await imageFilterService.saveFilterAsync(name, filtersDraft);
  };

  const handleFilterDeleteAsync = async (id: string): Promise<void> => {
    await imageFilterService.deleteFilterAsync(id);
  };

  const handleModalOpen = (): void => {
    setSelectedFilterTab(FilterTabType.InDatasets);
    setFiltersDraft(cloneDeep(imagesStore.imageFilters));
  };

  const activeFiltersCount = getActiveFiltersCount(filtersDraft);
  const isClearAllEnabled = activeFiltersCount > 0 || filtersDraft.sorting.some((x) => x.by !== undefined);

  return (
    <ImageFiltersForm
      activeFiltersCount={activeFiltersCount}
      isClearAllEnabled={isClearAllEnabled}
      showClearAllConfirmation={showClearAllConfirmed}
      savedFilters={imagesStore.savedFilters}
      isLoading={overlayLoaderStore.isSpinnerVisible('details-images-loading')}
      activeFilterType={selectedFilterTab}
      imageFilters={filtersDraft}
      availableFilters={imagesStore.availableFilters}
      onFilterTabSelect={handleFilterTabSelected}
      onSetAnnotatedBy={handleSetAnnotatedBy}
      onSetCorrected={handleSetCorrected}
      onSetImageSets={handleSetImageSets}
      onSetInDatasets={handleSetInDatasets}
      onSetStatuses={handleSetStatuses}
      onSetReviewedBy={handleSetReviewedBy}
      onSetAnnotationTools={handleSetAnnotationTools}
      onSetSorting={handleSetSorting}
      onClearAll={handleClearAll}
      onClearAllDeclined={handleClearAllDeclined}
      onClearAllConfirmedAsync={handleClearAllConfirmedAsync}
      onApplyAsync={handleApplyAsync}
      onSavedFilterLoad={handleSavedFilterLoad}
      onSavedFilterAsync={handleSavedFilterAsync}
      onFilterDeleteAsync={handleFilterDeleteAsync}
      onModalClose={props.onAfterFilter}
      onModalOpen={handleModalOpen}
      isModalOpen={props.isModalOpen}
    />
  );
});
